import { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Save, Plus } from 'lucide-react';
import { useProductStore, useCategoryStore, useUnitStore } from '../../../hooks';
import { Button, Card, CardContent, useToast } from '@lancar-pos/ui';
import type { ProductVariant } from '@lancar-pos/db-schema';
import { VariantManager } from './VariantManager';
import { EmojiPicker } from './EmojiPicker';

interface ProductFormProps {
  productId?: string;
  isNew: boolean;
}

export function ProductForm({ productId, isNew }: ProductFormProps) {
  const { products, addProduct, updateProduct } = useProductStore();
  const { categories, addCategory } = useCategoryStore();
  const { units, addUnit } = useUnitStore();
  const toast = useToast();
  
  const [isLoading, setIsLoading] = useState(false);

  // Inline form states
  const [showUnitForm, setShowUnitForm] = useState(false);
  const [newUnitName, setNewUnitName] = useState('');
  const [newUnitAbbreviation, setNewUnitAbbreviation] = useState('');
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#3B82F6');

  // Form state
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [barcode, setBarcode] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [unit, setUnit] = useState('pcs');
  const [price, setPrice] = useState('');
  const [cost, setCost] = useState('');
  const [imageUrl, setImageUrl] = useState('📦');
  const [stock, setStock] = useState('0');
  const [minStock, setMinStock] = useState('5');
  const [isActive, setIsActive] = useState(true);
  const [hasVariants, setHasVariants] = useState(false);
  const [variants, setVariants] = useState<ProductVariant[]>([]);

  // Load product data if editing
  useEffect(() => {
    if (!isNew && productId) {
      const product = products.find((p) => p.id === productId);
      if (product) {
        setName(product.name);
        setSku(product.sku);
        setBarcode(product.barcode);
        setCategoryId(product.categoryId || '');
        setUnit(product.unit);
        setPrice(product.price.toString());
        setCost(product.cost.toString());
        setImageUrl(product.imageUrl || '📦');
        setStock(product.stock.toString());
        setMinStock(product.minStock.toString());
        setIsActive(product.isActive);
        setHasVariants(product.variants && product.variants.length > 0);
        setVariants(product.variants || []);
      }
    }
  }, [isNew, productId, products]);

  // Generate SKU for variants
  const generateVariantSKU = useCallback((index: number) => {
    const baseId = productId || crypto.randomUUID().slice(0, 4);
    return `${baseId.toUpperCase()}-${String(index + 1).padStart(2, '0')}`;
  }, [productId]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Nama produk wajib diisi');
      return;
    }

    if (!price || isNaN(parseFloat(price)) || parseFloat(price) <= 0) {
      toast.error('Harga produk tidak valid');
      return;
    }

    setIsLoading(true);

    try {
      const productData = {
        name: name.trim(),
        sku: sku.trim(),
        barcode: barcode.trim(),
        categoryId: categoryId || null,
        unit,
        price: parseFloat(price),
        cost: parseFloat(cost) || 0,
        imageUrl: imageUrl === '📦' ? null : imageUrl,
        stock: hasVariants ? 0 : parseInt(stock, 10) || 0,
        minStock: parseInt(minStock, 10) || 0,
        isActive,
        variants: hasVariants ? variants : [],
        bomItems: [],
      };

      if (isNew) {
        await addProduct(productData);
        toast.success('Produk berhasil ditambahkan');
      } else if (productId) {
        await updateProduct(productId, productData);
        toast.success('Produk berhasil diperbarui');
      }

      window.history.back();
    } catch (error) {
      toast.error(isNew ? 'Gagal menambahkan produk' : 'Gagal memperbarui produk');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">
              {isNew ? 'Tambah Produk' : 'Edit Produk'}
            </h1>
          </div>
          <Button onClick={handleSave} loading={isLoading}>
            <Save className="w-4 h-4 mr-2" />
            Simpan
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 lg:p-6 space-y-6">
        <Card>
          <CardContent className="p-6 space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Informasi Dasar</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nama Produk *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Contoh: T-Shirt Basic"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SKU
                  </label>
                  <input
                    type="text"
                    value={sku}
                    onChange={(e) => setSku(e.target.value)}
                    placeholder="A1B2-00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">Biarkan kosong untuk generate otomatis</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Barcode
                  </label>
                  <input
                    type="text"
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    placeholder="Scan atau ketik barcode"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kategori
                  </label>
                  {showCategoryForm ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Nama Kategori"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                        <input
                          type="color"
                          value={newCategoryColor}
                          onChange={(e) => setNewCategoryColor(e.target.value)}
                          className="w-10 h-10 p-1 border border-gray-300 rounded-lg cursor-pointer"
                          title="Warna"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={async () => {
                            if (!newCategoryName.trim()) {
                              toast.error('Nama kategori wajib diisi');
                              return;
                            }
                            try {
                              const newCatId = crypto.randomUUID();
                              const now = new Date().toISOString();
                              await (window as any).db?.categories?.insert({
                                name: newCategoryName.trim(),
                                color: newCategoryColor,
                                icon: '📁',
                                parentId: null,
                                sortOrder: 0,
                                id: newCatId,
                                createdAt: now,
                                updatedAt: now,
                                _deleted: false,
                              }) || addCategory({
                                name: newCategoryName.trim(),
                                color: newCategoryColor,
                                icon: '📁',
                                parentId: null,
                                sortOrder: 0,
                              });
                              setCategoryId(newCatId);
                              setShowCategoryForm(false);
                              setNewCategoryName('');
                              toast.success('Kategori ditambahkan');
                            } catch (error) {
                              toast.error('Gagal menambahkan kategori');
                            }
                          }}
                        >
                          Simpan
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setShowCategoryForm(false);
                            setNewCategoryName('');
                          }}
                        >
                          Batal
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <select
                        value={categoryId}
                        onChange={(e) => setCategoryId(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Pilih kategori</option>
                        {categories
                          .filter((c) => !c._deleted)
                          .map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowCategoryForm(true)}
                        className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        title="Tambah Kategori"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Satuan
                  </label>
                  {showUnitForm ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Nama Satuan"
                          value={newUnitName}
                          onChange={(e) => setNewUnitName(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                        <input
                          type="text"
                          placeholder="Singkat"
                          value={newUnitAbbreviation}
                          onChange={(e) => setNewUnitAbbreviation(e.target.value)}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={async () => {
                            if (!newUnitName.trim() || !newUnitAbbreviation.trim()) {
                              toast.error('Nama dan singkat wajib diisi');
                              return;
                            }
                            try {
                              await addUnit({
                                name: newUnitName.trim(),
                                abbreviation: newUnitAbbreviation.trim(),
                                type: 'count',
                                isDefault: false,
                              });
                              setUnit(newUnitAbbreviation.trim());
                              setShowUnitForm(false);
                              setNewUnitName('');
                              setNewUnitAbbreviation('');
                              toast.success('Satuan ditambahkan');
                            } catch (error) {
                              toast.error('Gagal menambahkan satuan');
                            }
                          }}
                        >
                          Simpan
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setShowUnitForm(false);
                            setNewUnitName('');
                            setNewUnitAbbreviation('');
                          }}
                        >
                          Batal
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <select
                        value={unit}
                        onChange={(e) => setUnit(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      >
                        {units.filter((u) => !u._deleted).map((u) => (
                          <option key={u.id} value={u.abbreviation}>
                            {u.name} ({u.abbreviation})
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowUnitForm(true)}
                        className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        title="Tambah Satuan"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="space-y-4 pt-4 border-t">
              <h2 className="text-lg font-semibold text-gray-900">Harga</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Harga Jual *
                  </label>
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="50000"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Harga Modal
                  </label>
                  <input
                    type="number"
                    value={cost}
                    onChange={(e) => setCost(e.target.value)}
                    placeholder="30000"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Variants */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Varian</h2>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasVariants}
                    onChange={(e) => {
                      setHasVariants(e.target.checked);
                      if (e.target.checked && variants.length === 0) {
                        // Add initial variant
                        setVariants([
                          {
                            id: crypto.randomUUID(),
                            name: 'Default',
                            sku: generateVariantSKU(0),
                            priceModifier: 0,
                            stock: 0,
                            isActive: true,
                          },
                        ]);
                      }
                    }}
                    className="w-4 h-4 text-primary-600 rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Produk memiliki varian</span>
                </label>
              </div>

              {hasVariants && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <VariantManager
                    variants={variants}
                    onChange={setVariants}
                    basePrice={parseFloat(price) || 0}
                    productId={productId}
                    generateSKU={generateVariantSKU}
                  />
                </div>
              )}

              {!hasVariants && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stok
                    </label>
                    <input
                      type="number"
                      value={stock}
                      onChange={(e) => setStock(e.target.value)}
                      placeholder="0"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stok Minimum
                    </label>
                    <input
                      type="number"
                      value={minStock}
                      onChange={(e) => setMinStock(e.target.value)}
                      placeholder="5"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Icon & Status */}
            <div className="space-y-4 pt-4 border-t">
              <h2 className="text-lg font-semibold text-gray-900">Ikon & Status</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ikon Produk
                </label>
                <EmojiPicker value={imageUrl} onChange={setImageUrl} />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="w-4 h-4 text-primary-600 rounded border-gray-300"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">
                  Produk aktif (ditampilkan di POS)
                </label>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
